import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import api from '../../utils/api';

interface ValidationIssue {
  type: 'error' | 'warning' | 'info';
  message: string;
  field?: string;
}

interface PaymentValidationResult {
  clientId: string;
  clientName: string;
  isValid: boolean;
  issues: ValidationIssue[];
  calculated: {
    attendedSessions: number;
    totalSessionsValue: number;
    totalPaymentsMade: number;
    paymentsAfterAssessmentFee: number;
    calculatedOutstandingBalance: number;
  };
  current: {
    outstandingBalance: number;
    sessionRate: number;
  };
  discrepancy: number;
  canAutoFix: boolean;
}

interface ValidationSummary {
  totalClients: number;
  validClients: number;
  clientsWithIssues: number;
  clientsNeedingFix: number;
  totalDiscrepancy: number;
}

const PaymentDataIntegrity: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<ValidationSummary | null>(null);
  const [results, setResults] = useState<PaymentValidationResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [fixingClient, setFixingClient] = useState<string | null>(null);
  const [fixAllDialogOpen, setFixAllDialogOpen] = useState(false);
  const [fixingAll, setFixingAll] = useState(false);

  const runValidation = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await api.get('/api/payment-validation/validate-all');
      const data = response.data as { summary: ValidationSummary; results: PaymentValidationResult[] };

      setSummary(data.summary);
      setResults(data.results);

      if (data.summary.clientsWithIssues === 0) {
        setSuccess('All client payment data is valid! ✅');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to validate payment data');
    } finally {
      setLoading(false);
    }
  };

  const fixClient = async (clientId: string) => {
    try {
      setFixingClient(clientId);
      setError(null);
      setSuccess(null);

      const response = await api.post(`/api/payment-validation/fix/${clientId}`);
      const data = response.data as { success: boolean; message: string };

      setSuccess(data.message);

      // Refresh validation
      await runValidation();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fix client data');
    } finally {
      setFixingClient(null);
    }
  };

  const fixAllClients = async () => {
    try {
      setFixingAll(true);
      setError(null);
      setSuccess(null);

      const fixableClients = results.filter(r => r.canAutoFix);
      let fixed = 0;
      let failed = 0;

      for (const client of fixableClients) {
        try {
          await api.post(`/api/payment-validation/fix/${client.clientId}`);
          fixed++;
        } catch (err) {
          failed++;
        }
      }

      setSuccess(`Fixed ${fixed} client(s). ${failed > 0 ? `Failed: ${failed}` : ''}`);
      setFixAllDialogOpen(false);

      // Refresh validation
      await runValidation();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fix all clients');
    } finally {
      setFixingAll(false);
    }
  };

  const toggleRow = (clientId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(clientId)) {
      newExpanded.delete(clientId);
    } else {
      newExpanded.add(clientId);
    }
    setExpandedRows(newExpanded);
  };

  const formatCurrency = (pence: number) => {
    return `£${(pence / 100).toFixed(2)}`;
  };

  const getIssueIcon = (type: string) => {
    switch (type) {
      case 'error':
        return <ErrorIcon sx={{ color: '#f56565', fontSize: 18 }} />;
      case 'warning':
        return <WarningIcon sx={{ color: '#ed8936', fontSize: 18 }} />;
      default:
        return <WarningIcon sx={{ color: '#4299e1', fontSize: 18 }} />;
    }
  };

  useEffect(() => {
    runValidation();
  }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" className="theme-text-primary">
          Payment Data Integrity
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {results.length > 0 && results.some(r => r.canAutoFix) && (
            <Button
              variant="contained"
              startIcon={<BuildIcon />}
              onClick={() => setFixAllDialogOpen(true)}
              sx={{
                backgroundColor: 'rgba(79, 70, 229, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(79, 70, 229, 1)'
                }
              }}
            >
              Fix All Auto-Fixable
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={runValidation}
            disabled={loading}
            sx={{
              borderColor: 'rgba(79, 70, 229, 0.5)',
              color: 'rgba(255, 255, 255, 0.95)',
              '&:hover': {
                borderColor: 'rgba(79, 70, 229, 0.8)',
                backgroundColor: 'rgba(79, 70, 229, 0.1)'
              }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {summary && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)' }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Total Clients
              </Typography>
              <Typography variant="h4" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                {summary.totalClients}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ backgroundColor: 'rgba(72, 187, 120, 0.1)', backdropFilter: 'blur(10px)' }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Valid
              </Typography>
              <Typography variant="h4" sx={{ color: '#48bb78' }}>
                {summary.validClients}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ backgroundColor: 'rgba(245, 101, 101, 0.1)', backdropFilter: 'blur(10px)' }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                With Issues
              </Typography>
              <Typography variant="h4" sx={{ color: '#f56565' }}>
                {summary.clientsWithIssues}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ backgroundColor: 'rgba(237, 137, 54, 0.1)', backdropFilter: 'blur(10px)' }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Total Discrepancy
              </Typography>
              <Typography variant="h4" sx={{ color: '#ed8936' }}>
                {formatCurrency(summary.totalDiscrepancy)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {results.length > 0 && (
        <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Table>
            <TableHead sx={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}>
              <TableRow>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', width: 50 }} />
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Client</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Current Balance</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Calculated Balance</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Discrepancy</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Issues</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((result) => (
                <React.Fragment key={result.clientId}>
                  <TableRow>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <IconButton
                        size="small"
                        onClick={() => toggleRow(result.clientId)}
                        sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                      >
                        <ExpandMoreIcon
                          sx={{
                            transform: expandedRows.has(result.clientId) ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s'
                          }}
                        />
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      {result.clientName}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      {formatCurrency(result.current.outstandingBalance)}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      {formatCurrency(result.calculated.calculatedOutstandingBalance)}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Chip
                        label={formatCurrency(Math.abs(result.discrepancy))}
                        size="small"
                        sx={{
                          backgroundColor: Math.abs(result.discrepancy) > 100 ? 'rgba(245, 101, 101, 0.2)' : 'rgba(237, 137, 54, 0.2)',
                          color: Math.abs(result.discrepancy) > 100 ? '#f56565' : '#ed8936'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Chip
                        label={`${result.issues.length} issue(s)`}
                        size="small"
                        color={result.issues.some(i => i.type === 'error') ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      {result.canAutoFix && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => fixClient(result.clientId)}
                          disabled={fixingClient === result.clientId}
                          startIcon={fixingClient === result.clientId ? <CircularProgress size={16} /> : <BuildIcon />}
                          sx={{
                            backgroundColor: 'rgba(72, 187, 120, 0.8)',
                            '&:hover': {
                              backgroundColor: 'rgba(72, 187, 120, 1)'
                            }
                          }}
                        >
                          Fix
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Collapse in={expandedRows.has(result.clientId)} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, backgroundColor: 'rgba(0, 0, 0, 0.2)' }}>
                          <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
                            Issues:
                          </Typography>
                          {result.issues.map((issue, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              {getIssueIcon(issue.type)}
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                {issue.message}
                              </Typography>
                            </Box>
                          ))}
                          <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                            <Box>
                              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                Attended Sessions: {result.calculated.attendedSessions}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                Session Rate: £{result.current.sessionRate}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                Total Payments: {formatCurrency(result.calculated.totalPaymentsMade)}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                Sessions Value: {formatCurrency(result.calculated.totalSessionsValue)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && results.length === 0 && summary && (
        <Card sx={{ backgroundColor: 'rgba(72, 187, 120, 0.1)', backdropFilter: 'blur(10px)', textAlign: 'center', p: 4 }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: '#48bb78', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#48bb78' }}>
            All Payment Data is Valid!
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1 }}>
            No discrepancies found across {summary.totalClients} clients.
          </Typography>
        </Card>
      )}

      {/* Fix All Dialog */}
      <Dialog
        open={fixAllDialogOpen}
        onClose={() => !fixingAll && setFixAllDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
          Fix All Auto-Fixable Clients?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            This will automatically fix {results.filter(r => r.canAutoFix).length} client(s) by updating their outstanding balance to the calculated value.
          </Typography>
          {fixingAll && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFixAllDialogOpen(false)}
            disabled={fixingAll}
            sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={fixAllClients}
            disabled={fixingAll}
            variant="contained"
            sx={{
              backgroundColor: 'rgba(72, 187, 120, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(72, 187, 120, 1)'
              }
            }}
          >
            {fixingAll ? 'Fixing...' : 'Fix All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentDataIntegrity;
